import React, { useEffect, useRef, useState, useCallback } from "react"
import { useParams } from "react-router-dom"
import { API } from "../config/api"
import { listScenes, createScene, deleteScene, updateScene } from "../services/sceneService"
import "./ClassifyMovie.css"

const EMOTIONS = [
  "happy","amused","involved","inspired","tender",
  "surprise","astonished","sad","melancholic","bored",
  "compassioned","fear","disturbed","scared","disgust",
  "disappointed","embarrassed","angry","annoyed","nothing",
]

const EMOTION_COLORS = {
  happy:"#E7C239", amused:"#D93D57", involved:"#E34327", inspired:"#EC9965", tender:"#E65C9E",
  surprise:"#53A7BD", astonished:"#59A8C9", sad:"#3374B4", melancholic:"#3374B4", bored:"#95B6D8",
  compassioned:"#A8D4EA", fear:"#5BAF61", disturbed:"#418266", scared:"#428249", disgust:"#7556A5",
  disappointed:"#9678B6", embarrassed:"#3374B4", angry:"#975767", annoyed:"#D096A2", nothing:"#333333",
}

const fmt = (sec) => {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}m${String(s).padStart(2,"0")}s`
}

// Accepts a full YouTube URL or a raw 11-char id, returns the id (or "")
const ytId = (input) => {
  if (!input) return ""
  const s = String(input).trim()
  const m = s.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/)
  if (m) return m[1]
  return /^[\w-]{11}$/.test(s) ? s : ""
}

export default function ClassifyMovie() {
  const { id } = useParams()
  const [movie, setMovie]   = useState(null)
  const [scenes, setScenes] = useState([])
  const [loading, setLoading] = useState(true)

  // new-scene drag
  const barRef  = useRef(null)
  const dragRef = useRef(null)
  const [selection, setSelection] = useState(null)
  const [picker, setPicker]       = useState(null) // { startSec, endSec, midFrac, sceneId? }

  // picker form
  const [pickerEmotion,   setPickerEmotion]   = useState("happy")
  const [pickerIntensity, setPickerIntensity] = useState(5)
  const [pickerYt,        setPickerYt]        = useState("")
  const [saving, setSaving] = useState(false)
  const [hoveredScene, setHoveredScene] = useState(null) // { id, label, midFrac }

  // inline youtube association (scene cards)
  const [ytEditScene, setYtEditScene] = useState(null) // sceneId being edited
  const [ytInput, setYtInput]         = useState("")
  const [ytSaving, setYtSaving]       = useState(false)

  // resize drag
  const resizeRef = useRef(null) // { sceneId, edge: 'start'|'end', origSec }

  const durationSec = (movie?.duration || 0) * 60

  const reload = useCallback(async () => {
    const s = await listScenes(id)
    setScenes(s)
  }, [id])

  useEffect(() => {
    const load = async () => {
      try {
        const res = await API.get(`/movies/${id}`)
        setMovie(res.data)
      } finally {
        setLoading(false)
      }
    }
    load()
    reload()
  }, [id, reload])

  // ── helpers ──────────────────────────────────────────────
  const fracFromClientX = (clientX) => {
    const rect = barRef.current.getBoundingClientRect()
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
  }

  const maxEnd = scenes.reduce((m, s) => Math.max(m, s.endSec), durationSec || 1) || 1

  // two intervals overlap if one starts before the other ends
  const overlapsAny = (startSec, endSec, excludeId = null) =>
    scenes.some((s) =>
      s._id !== excludeId && startSec < s.endSec && endSec > s.startSec
    )

  // nearest boundary that constrains a resize handle
  const clampedSec = (newSec, scene, edge) => {
    const sorted = [...scenes].sort((a, b) => a.startSec - b.startSec)
    if (edge === "start") {
      // can't go past the end of the previous scene
      const prev = sorted.filter((s) => s._id !== scene._id && s.endSec <= scene.endSec)
      const limit = prev.length ? Math.max(...prev.map((s) => s.endSec)) : 0
      return Math.min(Math.max(newSec, limit), scene.endSec - 5)
    } else {
      // can't go past the start of the next scene
      const next = sorted.filter((s) => s._id !== scene._id && s.startSec >= scene.startSec)
      const limit = next.length ? Math.min(...next.map((s) => s.startSec)) : maxEnd
      return Math.max(Math.min(newSec, limit), scene.startSec + 5)
    }
  }

  // ── new-scene drag ────────────────────────────────────────
  const onBarMouseDown = (e) => {
    if (e.button !== 0) return
    // don't start new drag if clicking a resize handle
    if (e.target.closest(".cm-handle")) return
    e.preventDefault()
    const frac = fracFromClientX(e.clientX)
    dragRef.current = { startFrac: frac }
    setSelection({ startFrac: frac, endFrac: frac })
    setPicker(null)

    const onMove = (ev) => {
      const f = fracFromClientX(ev.clientX)
      const lo = Math.min(dragRef.current.startFrac, f)
      const hi = Math.max(dragRef.current.startFrac, f)
      const conflicts = overlapsAny(Math.round(lo * maxEnd), Math.round(hi * maxEnd))
      setSelection({ startFrac: lo, endFrac: f, conflict: conflicts })
    }
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
      const endFrac   = fracFromClientX(ev.clientX)
      const startFrac = dragRef.current.startFrac
      const lo = Math.min(startFrac, endFrac)
      const hi = Math.max(startFrac, endFrac)
      if (hi - lo < 0.005) { setSelection(null); return }
      const startSec = Math.round(lo * maxEnd)
      const endSec   = Math.round(hi * maxEnd)
      if (overlapsAny(startSec, endSec)) { setSelection(null); return }
      setSelection({ startFrac: lo, endFrac: hi })
      openNewPicker(startSec, endSec, (lo + hi) / 2)
    }
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
  }

  const openNewPicker = (startSec, endSec, midFrac) => {
    setPickerEmotion("happy")
    setPickerIntensity(5)
    setPickerYt("")
    setPicker({ startSec, endSec, midFrac, sceneId: null })
  }

  // ── associate a youtube video with an existing scene ──────
  const handleAddYoutube = async (scene) => {
    const vid = ytId(ytInput)
    if (!vid) {
      alert("Please paste a valid YouTube link or video ID.")
      return
    }
    setYtSaving(true)
    try {
      await updateScene(id, scene._id, { youtubeVideoId: vid })
      await reload()
      setYtEditScene(null)
      setYtInput("")
    } catch (err) {
      console.error("Failed to attach video:", err)
      alert(`Could not attach video: ${err?.response?.data?.message || err?.message}`)
    } finally {
      setYtSaving(false)
    }
  }

  // ── click existing scene ──────────────────────────────────
  const onSegmentClick = (e, scene) => {
    e.stopPropagation()
    if (resizeRef.current) return // was a resize drag, ignore
    const lo = scene.startSec / maxEnd
    const hi = scene.endSec   / maxEnd
    setSelection({ startFrac: lo, endFrac: hi })
    setPickerEmotion(scene.emotion)
    setPickerIntensity(scene.intensity)
    setPickerYt(scene.youtubeVideoId === "none" ? "" : scene.youtubeVideoId || "")
    setPicker({ startSec: scene.startSec, endSec: scene.endSec, midFrac: (lo + hi) / 2, sceneId: scene._id })
  }

  // ── resize handles ────────────────────────────────────────
  const onHandleMouseDown = (e, scene, edge) => {
    e.stopPropagation()
    e.preventDefault()
    resizeRef.current = { sceneId: scene._id, edge, moved: false }

    const onMove = (ev) => {
      resizeRef.current.moved = true
      const frac = fracFromClientX(ev.clientX)
      const newSec = Math.round(frac * maxEnd)
      setScenes((prev) => {
        const current = prev.find((s) => s._id === scene._id)
        if (!current) return prev
        const clamped = clampedSec(newSec, current, edge)
        return prev.map((s) => {
          if (s._id !== scene._id) return s
          if (edge === "start") return { ...s, startSec: clamped }
          return { ...s, endSec: clamped }
        })
      })
    }

    const onUp = async () => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
      if (!resizeRef.current.moved) { resizeRef.current = null; return }
      const updated = scenes.find((s) => s._id === scene._id) ||
        (await listScenes(id)).find((s) => s._id === scene._id)
      // read latest from state via setScenes callback
      setScenes((prev) => {
        const s = prev.find((x) => x._id === scene._id)
        if (s) {
          const patch = edge === "start"
            ? { startSec: s.startSec }
            : { endSec: s.endSec }
          updateScene(id, scene._id, patch).catch(console.error)
        }
        return prev
      })
      setTimeout(() => { resizeRef.current = null }, 50)
    }

    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
  }

  // ── save / update ─────────────────────────────────────────
  const onSave = async () => {
    if (!picker) return
    setSaving(true)
    try {
      if (picker.sceneId) {
        await updateScene(id, picker.sceneId, {
          emotion:   pickerEmotion,
          intensity: pickerIntensity,
          startSec:  picker.startSec,
          endSec:    picker.endSec,
          youtubeVideoId: pickerYt || "none",
        })
      } else {
        await createScene(id, {
          youtubeVideoId: pickerYt || "none",
          startSec: picker.startSec,
          endSec:   picker.endSec,
          emotion:  pickerEmotion,
          intensity: pickerIntensity,
        })
      }
      await reload()
      setPicker(null)
      setSelection(null)
      setPickerYt("")
    } catch (err) {
      console.error("Failed to save scene:", err)
      const msg = err?.response?.data?.message || err?.message || "Unknown error"
      alert(`Could not save scene: ${msg}`)
    } finally {
      setSaving(false)
    }
  }

  const onRemove = async (sceneId) => {
    await deleteScene(id, sceneId)
    setPicker(null)
    setSelection(null)
    await reload()
  }

  // ── timeline geometry ─────────────────────────────────────
  const dominantColor = EMOTION_COLORS[movie?.emotion?.[0]] || "#95B6D8"
  const tickCount  = Math.min(13, Math.floor((movie?.duration || 0) / 10) + 1)
  const tickValues = Array.from({ length: tickCount }, (_, i) =>
    Math.round((i / (tickCount - 1)) * (movie?.duration || 0))
  )

  if (loading) return <div className="cm-loading">Loading…</div>

  return (
    <div className="cm-page">

      {/* ── header ── */}
      <div className="cm-header">
        <h1 className="cm-title">{movie?.title}</h1>
        <div className="cm-meta">
          {movie?.director && <span>{movie.director}</span>}
          {movie?.duration && <span>{movie.duration} min</span>}
          <span className="cm-emotion-badge" style={{ background: dominantColor }}>
            {movie?.emotion?.[0] || "nothing"}
          </span>
        </div>
      </div>

      {/* ── timeline ── */}
      <div className="cm-timeline-wrap">
        <div className="cm-timeline-label">Emotional Timeline</div>

        <div
          ref={barRef}
          className="cm-bar"
          onMouseDown={onBarMouseDown}
          style={{ cursor: "crosshair" }}
        >
          {scenes.length === 0 && (
            <div className="cm-bar__base" style={{ background: dominantColor }} />
          )}

          {scenes.map((s) => {
            const left  = (s.startSec / maxEnd) * 100
            const width = ((s.endSec - s.startSec) / maxEnd) * 100
            const isSelected = picker?.sceneId === s._id
            return (
              <div
                key={s._id}
                className={`cm-seg ${isSelected ? "cm-seg--selected" : ""}`}
                style={{
                  left:   `${left}%`,
                  width:  `${width}%`,
                  background: EMOTION_COLORS[s.emotion] || "#333",
                  filter: `saturate(${(s.intensity / 5) * 100}%)`,
                  cursor: "pointer",
                }}
                onClick={(e) => onSegmentClick(e, s)}
                onMouseEnter={() => setHoveredScene({
                  id: s._id,
                  label: s.emotion,
                  color: EMOTION_COLORS[s.emotion] || "#333",
                  videoId: s.youtubeVideoId && s.youtubeVideoId !== "none" ? s.youtubeVideoId : null,
                  midFrac: (s.startSec + s.endSec) / 2 / maxEnd,
                })}
                onMouseLeave={() => setHoveredScene(null)}
              >
                {/* resize handles */}
                <div
                  className="cm-handle cm-handle--left"
                  onMouseDown={(e) => onHandleMouseDown(e, s, "start")}
                />
                <div
                  className="cm-handle cm-handle--right"
                  onMouseDown={(e) => onHandleMouseDown(e, s, "end")}
                />
              </div>
            )
          })}

          {selection && !picker?.sceneId && (
            <div
              className={`cm-selection ${selection.conflict ? "cm-selection--conflict" : ""}`}
              style={{
                left:  `${Math.min(selection.startFrac, selection.endFrac) * 100}%`,
                width: `${Math.abs(selection.endFrac - selection.startFrac) * 100}%`,
              }}
            />
          )}

          {/* hover preview — thumbnail + chevron when the scene has a video */}
          {hoveredScene && (
          <div
            className={`cm-hover-pop ${hoveredScene.videoId ? "cm-hover-pop--video" : ""}`}
            style={{ left: `clamp(120px, ${hoveredScene.midFrac * 100}%, calc(100% - 120px))` }}
          >
            {hoveredScene.videoId ? (
              <>
                <div className="cm-hover-thumb">
                  <img
                    src={`https://img.youtube.com/vi/${hoveredScene.videoId}/hqdefault.jpg`}
                    alt={hoveredScene.label}
                  />
                  <span className="cm-hover-chevron">❯</span>
                </div>
                <svg className="cm-hover-connector" viewBox="0 0 60 60" fill="none"
                     stroke={hoveredScene.color} strokeWidth="2.5" strokeLinecap="round">
                  <path d="M4 56 C 26 52, 30 20, 54 8" />
                  <circle cx="4" cy="56" r="3.5" fill={hoveredScene.color} stroke="none" />
                </svg>
                <span className="cm-hover-name" style={{ color: hoveredScene.color }}>
                  {hoveredScene.label}
                </span>
              </>
            ) : (
              <span className="cm-hover-name cm-hover-name--solo" style={{ color: hoveredScene.color }}>
                {hoveredScene.label}
              </span>
            )}
          </div>
          )}
        </div>

        {/* ticks */}
        <div className="cm-ticks">
          {tickValues.map((min) => (
            <div
              key={min}
              className="cm-tick"
              style={{ left: `${(min / (movie?.duration || 1)) * 100}%` }}
            >
              <span>{min}</span>
            </div>
          ))}
          <span className="cm-tick-unit">MIN</span>
        </div>

        {/* picker */}
        {picker && (
          <div
            className="cm-picker"
            style={{
              left: `clamp(12px, calc(${picker.midFrac * 100}% - 140px), calc(100% - 292px))`,
            }}
          >
            <div className="cm-picker__header">
              <span className="cm-picker__range">
                {fmt(picker.startSec)} – {fmt(picker.endSec)}
              </span>
              <button className="cm-picker__close" onClick={() => { setPicker(null); setSelection(null) }}>✕</button>
            </div>

            <div className="cm-picker__emotions">
              {EMOTIONS.map((emo) => (
                <button
                  key={emo}
                  className={`cm-picker__emo ${pickerEmotion === emo ? "cm-picker__emo--active" : ""}`}
                  style={{ "--emo-color": EMOTION_COLORS[emo] }}
                  onClick={() => setPickerEmotion(emo)}
                >
                  <span className="cm-picker__dot" style={{ background: EMOTION_COLORS[emo] }} />
                  {emo}
                </button>
              ))}
            </div>

            <div className="cm-picker__intensity">
              <span>Intensity</span>
              <div className="cm-intensity-btns">
                {[1,2,3,4,5].map((v) => (
                  <button
                    key={v}
                    className={`cm-intensity-btn ${pickerIntensity === v ? "cm-intensity-btn--active" : ""}`}
                    onClick={() => setPickerIntensity(v)}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <input
              className="cm-picker__yt"
              placeholder="YouTube video ID (optional)"
              value={pickerYt}
              onChange={(e) => setPickerYt(e.target.value)}
            />

            <div className="cm-picker__actions">
              <button className="cm-picker__save" onClick={onSave} disabled={saving}>
                {saving ? "Saving…" : picker.sceneId ? "Update scene" : "Save scene"}
              </button>
              {picker.sceneId && (
                <button className="cm-picker__delete" onClick={() => onRemove(picker.sceneId)}>
                  Remove
                </button>
              )}
            </div>
          </div>
        )}

        {scenes.length === 0 && !picker && (
          <p className="cm-hint">Click and drag on the timeline to classify a scene.</p>
        )}
      </div>

      {/* ── scene list ── */}
      {scenes.length > 0 && (
        <div className="cm-scenes">
          <h3 className="cm-scenes__title">Classified scenes</h3>
          <div className="cm-scenes__list">
            {scenes.map((s) => (
              <div
                key={s._id}
                className={`cm-scene-card ${picker?.sceneId === s._id ? "cm-scene-card--active" : ""}`}
                onClick={() => onSegmentClick({ stopPropagation: () => {} }, s)}
                style={{ cursor: "pointer" }}
              >
                <div className="cm-scene-card__stripe" style={{ background: EMOTION_COLORS[s.emotion] || "#333" }} />
                <div className="cm-scene-card__body">
                  <div className="cm-scene-card__top">
                    <div>
                      <span className="cm-scene-tag" style={{ borderColor: EMOTION_COLORS[s.emotion] }}>
                        <span className="cm-scene-dot" style={{ background: EMOTION_COLORS[s.emotion] }} />
                        {s.emotion}
                      </span>
                      <span className="cm-scene-intensity">intensity {s.intensity}</span>
                    </div>
                    <button className="cm-scene-del" onClick={(e) => { e.stopPropagation(); onRemove(s._id) }}>Remove</button>
                  </div>
                  <div className="cm-scene-time">{fmt(s.startSec)} – {fmt(s.endSec)}</div>

                  {/* has video → show player */}
                  {s.youtubeVideoId && s.youtubeVideoId !== "none" ? (
                    <div className="cm-scene-iframe">
                      <iframe
                        src={`https://www.youtube.com/embed/${s.youtubeVideoId}?start=${s.startSec}&end=${s.endSec}`}
                        title="scene"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : ytEditScene === s._id ? (
                    /* editing → link input */
                    <div className="cm-yt-edit" onClick={(e) => e.stopPropagation()}>
                      <input
                        className="cm-yt-input"
                        autoFocus
                        placeholder="Paste YouTube link or video ID…"
                        value={ytInput}
                        onChange={(e) => setYtInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAddYoutube(s)}
                      />
                      <button
                        className="cm-yt-submit"
                        disabled={ytSaving}
                        onClick={() => handleAddYoutube(s)}
                      >
                        {ytSaving ? "Adding…" : "Add YouTube link"}
                      </button>
                      <button className="cm-yt-cancel" onClick={() => { setYtEditScene(null); setYtInput("") }}>✕</button>
                    </div>
                  ) : (
                    /* idle → handwritten call-to-action on hover */
                    <button
                      className="cm-yt-cta"
                      style={{ color: EMOTION_COLORS[s.emotion] || "#333" }}
                      onClick={(e) => { e.stopPropagation(); setYtEditScene(s._id); setYtInput("") }}
                    >
                      <svg className="cm-yt-arrow" viewBox="0 0 40 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M2 4 C 10 2, 14 18, 26 16" />
                        <path d="M20 18 l 7 -2 l -3 6" />
                      </svg>
                      <span className="cm-yt-cta-text">add youtube video</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
